function waitForCartDrawerStyles() {
  const links = Array.from(document.querySelectorAll('link[data-cart-drawer-style]'));
  let cancel;
  const promise = new Promise((resolve, reject) => {
    const remaining = new Set(links);
    const listeners = [];
    let settled = false;
    let timeout;
    const finish = (ready, error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      listeners.forEach(([link, load, fail]) => {
        link.removeEventListener('load', load);
        link.removeEventListener('error', fail);
      });
      if (error) reject(error);
      else resolve(ready);
    };
    cancel = () => finish(false);
    if (links.length !== 6) {
      finish(false, new Error('Cart styles are unavailable'));
      return;
    }
    timeout = setTimeout(() => finish(false, new Error('Cart styles timed out')), 5000);
    for (const link of links) {
      const load = () => {
        link.dataset.cartStyleState = 'loaded';
        link.media = 'all';
        remaining.delete(link);
        if (!remaining.size) finish(true);
      };
      const fail = () => {
        link.dataset.cartStyleState = 'failed';
        finish(false, new Error('Cart styles failed to load'));
      };
      link.addEventListener('load', load);
      link.addEventListener('error', fail);
      listeners.push([link, load, fail]);
      if (link.dataset.cartStyleState === 'failed') fail();
      else if (link.dataset.cartStyleState === 'loaded' || link.sheet) load();
      if (settled) break;
    }
  });
  return { promise, cancel: () => cancel() };
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return Promise.resolve();
    if (this.pendingOpen) return this.pendingOpen.promise;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const styles = waitForCartDrawerStyles();
    const attempt = { canceled: false, cancelStyles: styles.cancel, trigger: triggeredBy || this.activeElement };
    attempt.previousBusy = attempt.trigger?.getAttribute('aria-busy');
    attempt.trigger?.setAttribute('aria-busy', 'true');
    const onEscape = (event) => {
      if (event.code === 'Escape' || event.key === 'Escape') this.close();
    };
    attempt.cleanup = () => {
      if (attempt.cleaned) return;
      attempt.cleaned = true;
      if (this.activeEscapeHandler !== onEscape) document.removeEventListener('keydown', onEscape);
      if (attempt.previousBusy === null || attempt.previousBusy === undefined) attempt.trigger?.removeAttribute('aria-busy');
      else attempt.trigger?.setAttribute('aria-busy', attempt.previousBusy);
      if (this.pendingOpen === attempt) this.pendingOpen = null;
    };
    document.addEventListener('keydown', onEscape);
    this.pendingOpen = attempt;
    attempt.promise = styles.promise
      .then((ready) => {
        if (!ready || attempt.canceled) return;
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
        return new Promise((resolve) => {
          attempt.finishAnimation = resolve;
          attempt.animationTimer = setTimeout(() => {
            if (!attempt.canceled) {
              this.onOpenTransitionEnd = () => {
                if (!this.classList.contains('active')) return;
                const container = this.classList.contains('is-empty')
                  ? this.querySelector('.drawer__inner-empty')
                  : document.getElementById('CartDrawer');
                const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
                trapFocus(container, focusElement);
              };
              this.addEventListener('transitionend', this.onOpenTransitionEnd, { once: true });
              this.activeEscapeHandler = onEscape;
              this.classList.add('animate', 'active');
              document.body.classList.add('overflow-hidden');
              this.querySelector('cart-drawer-items')?.dispatchViewEvent();
            }
            resolve();
          });
        });
      })
      .catch(() => {
        if (!attempt.canceled) window.location.assign(window.routes.cart_url);
      })
      .finally(attempt.cleanup);
    return attempt.promise;
  }

  close() {
    if (this.pendingOpen) {
      const attempt = this.pendingOpen;
      attempt.canceled = true;
      attempt.cancelStyles();
      clearTimeout(attempt.animationTimer);
      attempt.finishAnimation?.();
      attempt.cleanup();
    }
    if (this.activeEscapeHandler) {
      document.removeEventListener('keydown', this.activeEscapeHandler);
      this.activeEscapeHandler = null;
    }
    if (this.onOpenTransitionEnd) this.removeEventListener('transitionend', this.onOpenTransitionEnd);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    return this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
